import React, { useState, useRef, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import AuthModal from '../components/AuthModal';
import { Dialog, DialogContent } from '../components/ui/dialog';

const DemoChat = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [showAuth, setShowAuth] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const recognition = useRef(null);
  const audioRef = useRef(null);
  const audioInitialized = useRef(false);
  const hasShownWelcome = useRef(false);
  const speakTextRef = useRef(null);

  // Handle Subscribe button click
  const handleSubscribeClick = () => {
    if (user) {
      navigate('/checkout');
    } else {
      setShowAuth(true);
    }
  };

  // Show welcome message on load and speak it
  useEffect(() => {
    if (!hasShownWelcome.current) {
      hasShownWelcome.current = true;
      setTimeout(() => {
        const welcomeMsg = `👋 Hello! I'm Geni, your AI teacher! Welcome to Kids Genie AI Academy!

This is a FREE DEMO where you can chat with me and learn about how our interactive teaching works.

**What you can ask me about:**
• How does interactive AI teaching work?
• What will students learn in the courses?
• How can students interact with the AI teacher?
• What PDF materials are available?
• What makes our platform special?

**Want full access?** Subscribe to unlock:
✨ Complete 10-week AI & Coding courses
✨ Interactive lessons with me teaching step-by-step
✨ Downloadable student books (PDFs)
✨ Activities, quizzes, and projects

Go ahead, ask me anything about the platform! 💬`;
        
        setConversationHistory([{ role: 'assistant', content: welcomeMsg }]);
        
        // Automatically speak the welcome message after a short delay
        setTimeout(() => {
          if (speakTextRef.current) {
            speakTextRef.current(welcomeMsg);
          }
        }, 500);
      }, 1000);
    }
  }, []);

  useEffect(() => {
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognition.current = new SpeechRecognition();
      recognition.current.continuous = false;
      recognition.current.interimResults = false;
      recognition.current.lang = 'en-US';

      recognition.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setUserMessage(transcript);
        handleSendMessage(transcript);
      };

      recognition.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognition.current.onend = () => {
        setIsListening(false);
      };
    }
  }, []);

  const startListening = () => {
    if (recognition.current) {
      setIsListening(true);
      setUserMessage('');
      recognition.current.start();
    } else {
      alert('Speech recognition is not supported in your browser. Please type your message instead.');
    }
  };

  const stopListening = () => {
    if (recognition.current && isListening) {
      recognition.current.stop();
    }
  };

  const handleSendMessage = async (message) => {
    if (!message.trim()) return;

    setIsProcessing(true);

    // Add user message to history
    const newHistory = [...conversationHistory, { role: 'user', content: message }];
    setConversationHistory(newHistory);

    try {
      // Call chat API with demo context
      const demoContext = `You are Geni, an AI teacher giving a DEMO of the Kids Genie AI Academy platform. This is a FREE TRIAL chat where students learn about the platform.

Your role: Explain enthusiastically about:
- How interactive AI teaching works (you teach lessons, students can interrupt and ask questions)
- What courses we offer (10-week AI course, 10-week Coding course for ages 7-15)
- How students interact (voice or text, real-time responses, patient teaching)
- PDF student books available for download
- Fun, engaging, safe learning environment

IMPORTANT: If they ask about lessons or want to try actual teaching, tell them:
"That sounds exciting! To access the full interactive lessons where I teach you step-by-step, you'll need to subscribe. The subscription includes all 10 weeks of both AI and Coding courses, plus downloadable student books. Would you like to know more about subscribing?"

Keep responses friendly, encouraging, and informative. You're here to show them how amazing the platform is!`;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avatar/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: message,
          conversation_history: newHistory.slice(-6),
          lesson_context: demoContext
        })
      });

      if (!response.ok) throw new Error('Chat failed');

      const data = await response.json();
      const aiResponse = data.response;

      // Add AI response to history
      setConversationHistory([...newHistory, { role: 'assistant', content: aiResponse }]);

      // Speak the response
      await speakText(aiResponse);

    } catch (error) {
      console.error('Chat error:', error);
      alert('Failed to get response. Please try again!');
    } finally {
      setIsProcessing(false);
      setUserMessage('');
    }
  };

  const speakText = async (text) => {
    setIsSpeaking(true);

    try {
      if (!audioInitialized.current && audioRef.current) {
        audioRef.current.volume = 1.0;
        audioInitialized.current = true;
      }

      let textToSpeak = text;
      if (text.length > 4000) {
        textToSpeak = text.substring(0, 4000) + '...';
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/avatar/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text: textToSpeak, voice: 'alloy' })
      });

      if (response.ok) {
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);

        if (audioRef.current) {
          audioRef.current.src = audioUrl;
          
          try {
            await audioRef.current.play();
          } catch (playError) {
            console.error('Audio play error:', playError);
            setIsSpeaking(false);
          }
        }
      } else {
        setIsSpeaking(false);
      }
    } catch (error) {
      console.error('Speech error:', error);
      setIsSpeaking(false);
    }
  };
  
  // Store speakText in ref for use in effects
  speakTextRef.current = speakText;

  const handleAudioEnd = () => {
    setIsSpeaking(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #312e81 50%, #1e1b4b 100%)',
      padding: '12px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background decoration */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(147, 51, 234, 0.1) 0%, transparent 50%)',
        pointerEvents: 'none'
      }}></div>

      {/* Header - Mobile Responsive */}
      <div style={{
        textAlign: 'center',
        marginBottom: '20px',
        position: 'relative',
        zIndex: 1,
        padding: '0 10px'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'block',
            margin: '0 auto 15px auto',
            padding: '10px 20px',
            background: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            border: '2px solid rgba(255,255,255,0.2)',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600'
          }}
        >
          ← Back
        </button>
        
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(20px)',
          padding: '12px 16px',
          borderRadius: '16px',
          border: '2px solid rgba(255,255,255,0.2)',
          maxWidth: '100%'
        }}>
          <span style={{ fontSize: '28px' }}>🎓</span>
          <div>
            <h1 style={{
              color: 'white',
              margin: 0,
              fontSize: '18px',
              fontWeight: '700'
            }}>Chat with Geni - Free Demo</h1>
            <p style={{
              color: 'rgba(255,255,255,0.7)',
              margin: '3px 0 0 0',
              fontSize: '12px'
            }}>Interactive AI teaching platform</p>
          </div>
        </div>
      </div>

      {/* Main Container - Stack on mobile */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '20px',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Responsive grid styles */}
        <style>{`
          @media (min-width: 768px) {
            .demo-main-container {
              grid-template-columns: 1fr 1fr !important;
              gap: 30px !important;
            }
            .demo-geni-card {
              padding: 40px !important;
            }
            .demo-geni-video {
              height: 400px !important;
            }
            .demo-chat-panel {
              padding: 30px !important;
            }
          }
        `}</style>
        
        {/* Left: Geni */}
        <div className="demo-geni-card" style={{
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
          border: '2px solid rgba(255,255,255,0.1)',
          borderRadius: '20px',
          padding: '20px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {/* Geni Video */}
          <div className="demo-geni-video" style={{
            position: 'relative',
            width: '100%',
            height: '280px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'visible'
          }}>
            {/* Animated Floating Elements - Hidden on mobile */}
            <div className="hidden sm:block" style={{ position: 'absolute', top: '10%', left: '5%', animation: 'float 3s ease-in-out infinite', fontSize: '40px' }}>🤖</div>
            <div className="hidden sm:block" style={{ position: 'absolute', top: '20%', right: '8%', animation: 'float 4s ease-in-out infinite 0.5s', fontSize: '35px' }}>⭐</div>
            <div className="hidden sm:block" style={{ position: 'absolute', bottom: '15%', left: '10%', animation: 'float 3.5s ease-in-out infinite 1s', fontSize: '38px' }}>🎓</div>
            <div className="hidden sm:block" style={{ position: 'absolute', bottom: '25%', right: '5%', animation: 'float 4.5s ease-in-out infinite 1.5s', fontSize: '32px' }}>💡</div>
            <div className="hidden md:block" style={{ position: 'absolute', top: '35%', left: '2%', animation: 'float 3.8s ease-in-out infinite 0.8s', fontSize: '30px' }}>🚀</div>
            <div className="hidden md:block" style={{ position: 'absolute', top: '50%', right: '3%', animation: 'float 4.2s ease-in-out infinite 2s', fontSize: '36px' }}>🎨</div>
            <div className="hidden md:block" style={{ position: 'absolute', top: '70%', left: '8%', animation: 'float 3.3s ease-in-out infinite 0.3s', fontSize: '34px' }}>🎮</div>
            <div className="hidden md:block" style={{ position: 'absolute', bottom: '10%', right: '12%', animation: 'float 4s ease-in-out infinite 1.2s', fontSize: '40px' }}>🌟</div>
            
            {(isSpeaking || isListening) && (
              <div style={{
                position: 'absolute',
                inset: '-30px',
                background: `radial-gradient(circle, ${isListening ? 'rgba(255,215,0,0.3)' : 'rgba(0,217,255,0.3)'} 0%, transparent 70%)`,
                borderRadius: '50%',
                animation: 'pulse 2s ease-in-out infinite',
                filter: 'blur(30px)'
              }}></div>
            )}

            <video
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                filter: (isSpeaking || isListening) ? 'drop-shadow(0 0 30px rgba(0,217,255,0.8))' : 'none',
                position: 'relative',
                zIndex: 2
              }}
            >
              <source src="https://customer-assets.emergentagent.com/job_genie-learn/artifacts/pfidtz26_video.mp4" type="video/mp4" />
            </video>
          </div>

          {/* Status */}
          <div style={{
            textAlign: 'center',
            color: 'white',
            fontSize: '18px',
            fontWeight: '600'
          }}>
            {isSpeaking && '💬 Geni is speaking...'}
            {isListening && '🎤 Listening...'}
            {!isSpeaking && !isListening && '🎓 Ready to chat!'}
          </div>

          {/* Controls */}
          <div style={{ display: 'flex', gap: '15px' }}>
            <button
              onClick={isListening ? stopListening : startListening}
              disabled={isProcessing || isSpeaking}
              style={{
                flex: 1,
                padding: '18px',
                background: isListening 
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                  : 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: (isProcessing || isSpeaking) ? 'not-allowed' : 'pointer',
                opacity: (isProcessing || isSpeaking) ? 0.5 : 1
              }}
            >
              {isListening ? '🛑 Stop' : '🎤 Click to Speak'}
            </button>
          </div>

          {/* Text Input */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(userMessage)}
              placeholder="Or type your question here..."
              disabled={isProcessing || isSpeaking}
              style={{
                flex: 1,
                padding: '15px',
                background: 'rgba(255,255,255,0.1)',
                border: '2px solid rgba(255,255,255,0.2)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px'
              }}
            />
            <button
              onClick={() => handleSendMessage(userMessage)}
              disabled={!userMessage.trim() || isProcessing || isSpeaking}
              style={{
                padding: '15px 30px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: '700',
                cursor: (!userMessage.trim() || isProcessing || isSpeaking) ? 'not-allowed' : 'pointer',
                opacity: (!userMessage.trim() || isProcessing || isSpeaking) ? 0.5 : 1
              }}
            >
              Send
            </button>
          </div>

          {/* Subscribe CTA */}
          <div style={{
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            padding: '20px',
            borderRadius: '15px',
            textAlign: 'center'
          }}>
            <p style={{ color: 'white', fontWeight: '700', fontSize: '16px', margin: '0 0 10px 0' }}>
              🌟 Want Full Access?
            </p>
            <button
              onClick={handleSubscribeClick}
              style={{
                padding: '12px 30px',
                background: 'white',
                color: '#d97706',
                border: 'none',
                borderRadius: '10px',
                fontSize: '16px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Subscribe Now
            </button>
          </div>
        </div>

        {/* Right: Conversation */}
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          backdropFilter: 'blur(20px)',
          border: '2px solid rgba(255,255,255,0.1)',
          borderRadius: '20px',
          padding: '30px',
          height: '700px',
          overflow: 'auto'
        }}>
          <h3 style={{ color: 'white', marginTop: 0 }}>💬 Conversation</h3>
          
          {conversationHistory.map((msg, idx) => (
            <div
              key={idx}
              style={{
                marginBottom: '20px',
                padding: '15px',
                background: msg.role === 'user' 
                  ? 'rgba(59, 130, 246, 0.2)'
                  : 'rgba(147, 51, 234, 0.2)',
                borderRadius: '12px',
                color: 'white',
                whiteSpace: 'pre-wrap'
              }}
            >
              <div style={{ fontWeight: '700', marginBottom: '8px' }}>
                {msg.role === 'user' ? '👤 You' : '🎓 Geni'}
              </div>
              {msg.content}
            </div>
          ))}
        </div>
      </div>

      {/* Audio element */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnd}
        style={{ display: 'none' }}
      />

      {/* Auth Modal */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md">
          <AuthModal 
            onClose={() => setShowAuth(false)} 
            onAuthSuccess={() => {
              setShowAuth(false);
              navigate('/checkout');
            }}
            skipNavigate={true}
          />
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }
        @keyframes float {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg);
            opacity: 0.7;
          }
          50% { 
            transform: translateY(-20px) rotate(10deg);
            opacity: 1;
          }
        }
        
        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .demo-main-grid {
            grid-template-columns: 1fr !important;
          }
          .demo-header-title h1 {
            font-size: 18px !important;
          }
          .demo-header-title p {
            font-size: 12px !important;
          }
          .demo-geni-card {
            padding: 15px !important;
          }
          .demo-geni-video {
            height: 250px !important;
          }
          .demo-chat-card {
            padding: 15px !important;
            max-height: 400px !important;
          }
          .demo-floating-emoji {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DemoChat;
